import React, { useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Link from '@atlaskit/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { Theme as ButtonTheme } from '@atlaskit/button/custom-theme-button';
import Heading from '@atlaskit/heading';
import { modalButtonTheme } from '@atlaskit/onboarding';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';

import onboardingSvg from './assets/DatabaseGAOnboarding.svg';

const i18n = defineMessages({
	close: {
		id: 'databases-onboarding-ga.editor-table.close',
		defaultMessage: 'Maybe later',
		description: 'Close the onboarding popup (appears as a button)',
	},
	cta: {
		id: 'databases-onboarding-ga.editor-table.cta',
		defaultMessage: 'Try databases',
		description: 'Call to action to try databases (appears as a button)',
	},
	heading: {
		id: 'databases-onboarding-ga.editor-table.heading',
		defaultMessage: 'Need a smarter table?',
		description: 'Heading for the onboarding popup',
	},
	body: {
		id: 'databases-onboarding-ga.editor-table.body',
		defaultMessage:
			'Databases can organize, track, and sync data in a table layout. <a>Explore databases</a>',
		description:
			'Body text for the onboarding popup. Text inside <a> provides a link to more information about databases',
	},
});

const SCREEN_NAME = 'databaseGAEditorSpotlight';

export const EditorDatabasesOnboardingSpotlight = ({
	onCreateDatabase = () => {},
	onDismiss = () => {},
}: {
	onDismiss?: () => void;
	onCreateDatabase?: () => void;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: SCREEN_NAME,
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleTryDatabasesClick = useCallback(async () => {
		onCreateDatabase();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryDatabases',
				eventType: 'ui',
				source: SCREEN_NAME,
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onCreateDatabase]);

	const dismissOnboarding = useCallback(async () => {
		onDismiss();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismiss',
				eventType: 'ui',
				source: SCREEN_NAME,
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onDismiss]);

	const handleExploreDatabasesClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'exploreDatabases',
				eventType: 'ui',
				source: SCREEN_NAME,
			},
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		<ButtonTheme.Provider value={modalButtonTheme}>
			<Box
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				xcss={xcss({
					borderColor: 'color.border',
					borderStyle: 'solid',
					borderRadius: 'border.radius',
					borderWidth: 'border.width',

					backgroundColor: 'elevation.surface',

					position: 'fixed',
					right: 'space.500',
					bottom: 'space.500',

					// @ts-ignore
					// eslint-disable-next-line @compiled/no-suppress-xcss -- Ignored via go/DSP-18766
					width: 340,
					// @ts-ignore
					// eslint-disable-next-line @compiled/no-suppress-xcss -- Ignored via go/DSP-18766
					zIndex: 1,
				})}
			>
				<Stack>
					<Box>
						<img
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								height: '100%',
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								width: '100%',
							}}
							src={onboardingSvg}
							alt=""
						/>
					</Box>
					<Box
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
						xcss={xcss({
							padding: 'space.200',
						})}
					>
						<Stack space="space.200">
							<Heading size="medium">
								<FormattedMessage {...i18n.heading} />
							</Heading>
							<div>
								<FormattedMessage
									{...i18n.body}
									values={{
										a: (chunks: React.ReactNode[]) =>
											fg('dst-a11y__replace-anchor-with-link__content-types') ? (
												<Link
													target="_blank"
													onClick={handleExploreDatabasesClick}
													href="https://www.atlassian.com/software/confluence/databases"
												>
													{chunks}
												</Link>
											) : (
												// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
												<a
													target="_blank"
													onClick={handleExploreDatabasesClick}
													href="https://www.atlassian.com/software/confluence/databases"
												>
													{chunks}
												</a>
											),
									}}
								/>
							</div>
							<Inline space="space.100">
								<Button onClick={dismissOnboarding}>
									<FormattedMessage {...i18n.close} />
								</Button>
								<Button appearance="primary" onClick={handleTryDatabasesClick}>
									<FormattedMessage {...i18n.cta} />
								</Button>
							</Inline>
						</Stack>
					</Box>
				</Stack>
			</Box>
		</ButtonTheme.Provider>
	);
};
